import { Box, Typography, Tabs, Tab, Chip, useMediaQuery, Fade } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useSwipeable } from "react-swipeable";

const PitchSession_Preview = ({ pitchType, aiProfile }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation("pitch");

  const lang = i18n.language.startsWith("es") ? "es" : "en";
  const description = lang === "es" ? pitchType.description_es : pitchType.description_en;
  const objective = lang === "es" ? pitchType.objective_es : pitchType.objective_en;
  const structure = lang === "es" ? pitchType.pitch_structure_es : pitchType.pitch_structure_en;
  const difficulty_level = pitchType.difficulty_level && pitchType.difficulty_level > 0 ? pitchType.difficulty_level : 1;
  const experience_count = pitchType.experience_count && pitchType.experience_count > 0 ? pitchType.experience_count : 1;;

  const getExperienceLabel = (count) => {
    try {
      if (isNaN(count)) return "<10";
      if (count >= 1000) return "+1000";
      if (count >= 500) return "+500";
      if (count >= 100) return "+100";
      if (count >= 50) return "+50";
      if (count >= 10) return "+10";
      return "<10";
    } catch (e) {
      console.warn("Error in getExperienceLabel:", e);
      return "<10";
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setTabIndex((prev) => Math.min(prev + 1, 2)),
    onSwipedRight: () => setTabIndex((prev) => Math.max(prev - 1, 0)),
    trackTouch: true,
    preventDefaultTouchmoveEvent: true,
  });

  return (
    <Box display="flex" justifyContent="center">
      <Box
        sx={{
          borderRadius: 4,
          backgroundColor: "rgba(0,0,0,0.55)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          color: "#fff",
          py: 1,
          px: 2,
          mt: 3,
          mb: 0,
          minHeight: isMobile ? 360 : 360,
          maxHeight: isMobile ? 360 : 360,
          maxWidth: isMobile ? "100%" : 500,
          width: "100%",
          overflowY: "auto",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(e, newVal) => setTabIndex(newVal)}
          textColor="inherit"
          indicatorColor="secondary"
          TabIndicatorProps={{
            style: {
              backgroundColor: "#a855f7",
              height: "3px",
            },
          }}
          sx={{
            mb: 2,
            "& .MuiTab-root": {
              color: "#ccc",
              fontWeight: 600,
              "&.Mui-selected": { color: "#fff" },
              "&:hover": { color: "#e9d5ff" },
            },
          }}
        >
          <Tab label={t("pitchsessionpreview.tab_goal")} />
          <Tab label={t("pitchsessionpreview.tab_info")} />
          <Tab label={t("pitchsessionpreview.tab_structure")} />
        </Tabs>

        <Box sx={{ minHeight: 120 }} {...swipeHandlers}>
          {tabIndex === 0 && (
            <>
              <Typography variant="subtitle1" sx={{ color: "#a855f7", fontWeight: 600, mb: 0.5, mt: 3 }}>
                {t("pitchsessionpreview.title_objective")}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5, mt: 2, textAlign: "justify" }}>
                {objective}
              </Typography>
            </>
          )}

          {tabIndex === 1 && (
            <>

              <Typography variant="subtitle1" sx={{ color: "#a855f7", fontWeight: 500, mt: 3, mb: 0.5 }}>
                {t("pitchsessionpreview.title_description")}
              </Typography>
              <Typography variant="body1" sx={{ textAlign: "justify" }}>{description}</Typography>

              {/* Dificultad + Experiencia */}
              <Box display="flex" flexDirection="column" gap={0.5} mt={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle1" sx={{ color: "#a855f7", fontWeight: 500, flex: 1 }}>
                    {t("pitchsessionpreview.label_difficulty")}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: "#a855f7", fontWeight: 500, flex: 1 }}>
                    {t("pitchsessionpreview.label_experience_count")}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body1" sx={{ flex: 1 }}>
                    {t(`pitchsessionpreview.difficulty_${["easy", "medium", "hard"][difficulty_level - 1]}`)}
                  </Typography>
                  <Typography variant="body1" sx={{ flex: 1 }}>
                    {getExperienceLabel(experience_count)}
                  </Typography>
                </Box>
              </Box>
              {/* Duración estimada */}
              <Box mt={1}>
                <Typography variant="subtitle1" sx={{ color: "#a855f7", fontWeight: 500 }}>
                  {t("pitchsessionpreview.structure_duration_estimate")}
                </Typography>
                <Typography variant="body1">
                  {pitchType.pitch_duration_minutes + pitchType.qna_duration_minutes} {t("pitchsessionpreview.structure_duration_minutes")}
                </Typography>
              </Box>
            </>
          )}

          {tabIndex === 2 && (
            <Fade in={tabIndex === 2} timeout={500}>
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Typography variant="subtitle1" align="center" sx={{ color: "#fff", fontWeight: 500, mt: 1 }}>
                  {t("pitchsessionpreview.structure_intro")}
                </Typography>
                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                  {structure?.map((step, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                        <Chip
                          label={step}
                          sx={{
                            backgroundColor: "#c4b5fd", //"#8b5cf6",
                            color: "#333333", //#f5f3ff
                            fontWeight: 600,
//                            fontSize: "1rem", // más grande
                            mb: 1,
                            py: 1,
                          }}
                        />
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PitchSession_Preview;
